import {
  KeyboardEvent,
  Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';
import { usePopper } from 'react-popper';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Placement } from '@popperjs/core';

import { Icon } from '@/components';
import styles from './select.module.scss';

export interface ISelectOption<T> {
  label: string;
  value: T;
}

interface ISelectProps<T> {
  options: ISelectOption<T>[];
  value?: ISelectOption<T>;
  defaultValue?: ISelectOption<T>;
  name?: string;
  placeholder?: string;
  className?: string;
  placeholderLabel?: string;
  menuClassName?: string;
  placeholderLabelClassName?: string;
  defaultSelectedLabelClassName?: string;
  menuOptionClassName?: string;
  selectLabelClassName?: string;
  feedback?: string;
  isInvalid?: boolean;
  placement?: Placement;
  onChange: (_option: ISelectOption<T>) => void;
  getSelectedLabel?: (_option?: ISelectOption<T>) => string | undefined;
}

const Select = <T,>({
  name = 'select',
  className,
  menuClassName,
  selectLabelClassName,
  placeholderLabelClassName,
  defaultSelectedLabelClassName,
  menuOptionClassName,
  options,
  placeholderLabel,
  placeholder = '',
  defaultValue,
  value,
  placement,
  isInvalid,
  feedback,
  onChange,
  getSelectedLabel,
}: ISelectProps<T>) => {
  const isControlled = value !== undefined;
  const [internalSelected, setInternalSelected] = useState<
    ISelectOption<T> | undefined
  >(defaultValue);
  const selectedOption = isControlled ? value : internalSelected;

  const selectedLabel = useMemo(
    () => getSelectedLabel?.(selectedOption) ?? selectedOption?.label,
    [selectedOption, getSelectedLabel],
  );
  const [button, setButton] = useState<HTMLButtonElement | undefined>();
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const listboxRef = useRef<HTMLDivElement>(null);

  const { styles: popperStyles, attributes } = usePopper(button, panel, {
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 5] },
      },
      {
        name: 'preventOverflow',
        options: { padding: 16 },
      },
    ],
    placement: placement ?? 'bottom-start',
  });

  const getSelectedIndex = (optionOverride?: ISelectOption<T>) => {
    const target = optionOverride ?? selectedOption;
    const indexToFocus = options.findIndex(o => o.value === target?.value) ?? 0;
    return indexToFocus > 0 ? indexToFocus : 0;
  };

  const handleSelection = (option: ISelectOption<T>) => {
    if (!isControlled) {
      setInternalSelected(option);
    }
    onChange(option);
    setOpenIndex(getSelectedIndex(option));
    button?.click();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = openIndex ?? getSelectedIndex();
    e.preventDefault();

    switch (e.key) {
      case 'ArrowDown':
        setOpenIndex((currentIndex + 1) % options.length);
        break;

      case 'ArrowUp':
        setOpenIndex((currentIndex - 1 + options.length) % options.length);
        break;

      case 'Enter':
        if (openIndex != null) {
          handleSelection(options[openIndex]);
        }
        break;

      case 'Escape':
        setOpenIndex(null);
        button?.click(); // Close popover
        break;

      case 'Tab':
        setOpenIndex(null); // Close on Tab
        break;
    }
  };

  useEffect(() => {
    if (openIndex !== null && listboxRef.current) {
      const children = listboxRef.current.children;
      const element = children[openIndex] as HTMLElement;
      element?.focus();
      element?.scrollIntoView({ block: 'nearest' }); // scroll a opción visible
    }
  }, [openIndex]);

  const listboxId = `${name}-listbox`;

  return (
    <Popover className={classNames(className, styles.select)}>
      {({ open }) => (
        <>
          <PopoverButton
            aria-controls={listboxId}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={
              selectedLabel
                ? `Seleccionado: ${selectedLabel}`
                : placeholder
                ? `Seleccione una opción: ${placeholder}`
                : 'Seleccione una opción'
            }
            className={classNames(styles.select__trigger, {
              [styles.select__invalid]: isInvalid,
              [styles.selected]: selectedLabel,
            })}
            name={name}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                button?.click();
                setOpenIndex(getSelectedIndex());
              }
            }}
            ref={setButton as Ref<HTMLButtonElement>}
            role="button"
            tabIndex={0}
          >
            <span
              className={classNames(
                selectLabelClassName,
                styles.select__label,
                {
                  [defaultSelectedLabelClassName as string]:
                    value?.value === 'default',
                },
              )}
            >
              {placeholderLabel && selectedOption?.value !== 'default' && (
                <span className={placeholderLabelClassName}>
                  {placeholderLabel}
                </span>
              )}
              {selectedLabel ?? placeholder}
            </span>
            <Icon className={styles.select__icon} name="angle-down" />
          </PopoverButton>

          <PopoverPanel
            className={classNames(styles.select__menu, menuClassName)}
            id={listboxId}
            ref={setPanel as Ref<HTMLDivElement>}
            style={popperStyles.popper}
            {...attributes.popper}
          >
            <div
              className={styles.select__menu_body}
              id={listboxId}
              onKeyDown={handleKeyDown}
              ref={listboxRef}
              role="listbox"
              tabIndex={-1}
            >
              {options.map((option, index) => {
                const isFocused = index === openIndex;
                const isSelected = option.value === selectedOption?.value;

                return (
                  <div
                    aria-selected={isSelected}
                    className={classNames(
                      menuOptionClassName,
                      styles['select__menu-item'],
                      {
                        [styles['is-selected']]: isSelected,
                        [styles['is-focused']]: isFocused,
                      },
                    )}
                    key={index}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelection(option);
                    }}
                    ref={el => {
                      if (isFocused && el) {
                        el.focus();
                      }
                    }}
                    role="option"
                    tabIndex={isFocused ? 0 : -1}
                  >
                    {option.label}
                  </div>
                );
              })}
            </div>
          </PopoverPanel>

          {isInvalid && feedback && (
            <p className={styles.select__feedback}>
              <Icon name="info" /> {feedback}
            </p>
          )}
        </>
      )}
    </Popover>
  );
};

export { Select };
